import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import _ from "lodash";

import { useTranslation } from "react-i18next";
import { FixedSizeList } from "react-window";

import { Input, Button, Grid, Checkbox } from "semantic-ui-react";
import "../GroupSettings/SubgroupDropdown/SubGroupMaterials.css";

const ITEM_HEIGHT = 28;
const LIST_HEIGHT = 296;
const DIVIDER_SENTINEL = { isDivider: true, key: "__divider__" };

const labelStyle = {
    display: "block",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
};

const RowItem = React.memo(function RowItem({ option, style, isChecked, onCheck }) {
    return (
        <div className="subgroup item material-row-item" style={{ ...style, width: "max-content", minWidth: style.width }}>
            <div className="ui checkbox">
                <input
                    id={option.key}
                    checked={isChecked}
                    type="checkbox"
                    onChange={(e) => onCheck(e.target.checked, option.value)}
                />
                {" "}
                <label htmlFor={option.key} style={labelStyle} title={option.text}>{option.text}</label>
            </div>
        </div>
    );
});
RowItem.propTypes = {
    option: PropTypes.shape({ key: PropTypes.string, value: PropTypes.any, text: PropTypes.string }),
    style: PropTypes.object,
    isChecked: PropTypes.bool,
    onCheck: PropTypes.func,
};

function Row({ index, style, data }) {
    const { options, valuesSet, onCheck } = data;
    const option = options[index];
    if (option.isDivider) {
        return (
            <div style={{ ...style, display: "flex", alignItems: "center" }}>
                <div className="material-list-divider" />
            </div>
        );
    }
    return (
        <RowItem
            option={option}
            style={style}
            isChecked={valuesSet.has(option.value)}
            onCheck={onCheck}
        />
    );
}
Row.propTypes = {
    index: PropTypes.number,
    style: PropTypes.object,
    data: PropTypes.shape({
        options: PropTypes.array,
        valuesSet: PropTypes.instanceOf(Set),
        onCheck: PropTypes.func,
    }),
};

const Filters = React.memo(function Filters({ allOptions, setValues, onChange, t }) {
    return (
        <Grid.Row verticalAlign="middle" style={{ marginBottom: "5px" }}>
            <Button
                className="dropdown-filters"
                disabled={false}
                onClick={(e) => {
                    e.preventDefault();
                    const all = allOptions.map(option => option.value);
                    setValues(all);
                    onChange(all);
                }}
            >
                {t("groupSettings.selectAll")}
            </Button>
            <Button
                className="dropdown-filters"
                disabled={false}
                onClick={(e) => {
                    e.preventDefault();
                    setValues([]);
                }}
            >
                {t("groupSettings.unselectAll")}
            </Button>
        </Grid.Row>
    );
});
Filters.propTypes = {
    allOptions: PropTypes.array,
    setValues: PropTypes.func,
    onChange: PropTypes.func,
    t: PropTypes.func,
};

const MaterialCheckboxSelector = ({ options, defaultValues, onChange, defaultAlign }) => {
    const { t } = useTranslation();

    useEffect(() => {
        setValues([...defaultValues]);
    }, [defaultValues]);

    const [filterInput, setFilterInput] = useState("");
    const [filteredOptions, setFilteredOptions] = useState([...options]);
    const [values, setValues] = useState([...defaultValues]);
    const [sortByChecked, setSortByChecked] = useState(true);
    const listRef = useRef(null);
    const valuesRef = useRef(values);

    useEffect(() => {
        valuesRef.current = values;
    }, [values]);

    const valuesSet = useMemo(() => new Set(values), [values]);

    const handleFilterChange = (e) => {
        const value = e.target.value || undefined;
        setFilterInput(value);
        if (_.isNil(value)) {
            setFilteredOptions([...options]);
            return;
        }
        setFilteredOptions(options.filter(
            option => option.text.toLowerCase().includes(value.toLowerCase())
        ));
    };

    const handleCheck = useCallback((checked, optionValue) => {
        const prev = valuesRef.current;
        const next = checked ? [...prev, optionValue] : prev.filter(v => v !== optionValue);
        setValues(next);
        onChange(next);
    }, [onChange]);

    const sortedOptions = useMemo(() => (
        [...filteredOptions].sort((a, b) => {
            const aChecked = valuesSet.has(a.value);
            const bChecked = valuesSet.has(b.value);
            if (aChecked === bChecked) return 0;
            return aChecked ? -1 : 1;
        })
    ), [filteredOptions, valuesSet]);

    const baseOptions = sortByChecked ? sortedOptions : filteredOptions;

    const displayOptions = useMemo(() => {
        if (!sortByChecked) return baseOptions;
        const idx = baseOptions.findIndex(opt => !valuesSet.has(opt.value));
        if (idx <= 0) return baseOptions;
        return [...baseOptions.slice(0, idx), DIVIDER_SENTINEL, ...baseOptions.slice(idx)];
    }, [baseOptions, valuesSet, sortByChecked]);

    const itemData = useMemo(() => ({
        options: displayOptions,
        valuesSet,
        onCheck: handleCheck,
    }), [displayOptions, valuesSet, handleCheck]);

    return (
        <div>
            {defaultAlign && <Filters allOptions={options} setValues={setValues} onChange={onChange} t={t} />}
            <Grid.Row verticalAlign="top" stretched>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                    <Input
                        className="list-searchbar"
                        icon="search"
                        value={filterInput}
                        onChange={handleFilterChange}
                        placeholder={t("ordersTable.search")}
                        size="small"
                        style={{ flex: 1 }}
                    />
                    <Checkbox
                        toggle
                        label={t("groupSettings.sortByChecked")}
                        checked={sortByChecked}
                        onChange={(e, data) => setSortByChecked(data.checked)}
                        className="sort-toggle-btn"
                    />
                </div>
                <div style={{ width: "100%", overflow: "hidden", margin: "1em 0" }}>
                    <FixedSizeList
                        ref={listRef}
                        height={Math.min(LIST_HEIGHT, displayOptions.length * ITEM_HEIGHT)}
                        itemCount={displayOptions.length}
                        itemSize={ITEM_HEIGHT}
                        itemData={itemData}
                        width="100%"
                        overscanCount={5}
                    >
                        {Row}
                    </FixedSizeList>
                </div>
            </Grid.Row>
            {!defaultAlign && <Filters allOptions={options} setValues={setValues} onChange={onChange} t={t} />}
        </div>
    );
};

MaterialCheckboxSelector.propTypes = {
    options: PropTypes.array,
    defaultValues: PropTypes.array,
    onChange: PropTypes.func,
    groupType: PropTypes.string,
    defaultAlign: PropTypes.bool,
};

MaterialCheckboxSelector.defaultProps = {
    options: [],
    defaultValues: [],
    onChange: _.noop,
    required: false,
    groupType: "",
    defaultAlign: true,
};

export default React.memo(MaterialCheckboxSelector);
